import { ID } from '@ianesthesie-kit/services';
import { doctorBffApi, InvalidateArr } from '@/modules';
import {
  GenericPrescription,
  GenericPrescriptionPayload,
  LabOrderItem,
  Protocol,
  ProtocolItem,
  ProtocolItemOptionForName,
  ProtocolItemOptionUnion
} from './protocol.model';
import {
  ProtocolItemPayload,
  ProtocolPostPayload,
  ProtocolService
} from './protocol.service';
import {
  PostopChronicPainProtocolStatus,
  ProtocolName,
  ProtocolStatuses,
  ProtocolStatusesWithMeta,
  StatusTransitionMap
} from './protocol-status.model';
import {
  dashboardApi,
  Listed,
  PostopChronicPainDto,
  PreopChronicPainDto,
  PreopSurgeryPlanningDto,
  SurgeryPlanningParams,
  WithPatientId
} from '@/modules/surgicalEvent';
import { SentryService } from '@ianesthesie-kit/sentry';
import { imperativelyShowPromiseDialog } from '@ianesthesie-kit/redux';
import type { RootState } from '@/store';
import type { BaseQueryApi } from '@reduxjs/toolkit/dist/query/react';
import { AppDialogType } from '@/config';

export const protocolApi = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    protocolItemOptions: builder.query<
      ProtocolItemOptionUnion[],
      { protocol?: ProtocolName }
    >({
      providesTags: ['HospitalSpecificData'],
      queryFn: ({ protocol }) =>
        ProtocolService.getInstance()
          .fetchProtocolItemOptionList(protocol)
          .then((r) => ({
            data: r.data
          }))
          .catch((error) => ({ error }))
    }),
    labOrderPresets: builder.query<string[], void>({
      providesTags: ['HospitalSpecificData'],
      queryFn: () =>
        ProtocolService.getInstance()
          .fetchLabOrderPresetNames()
          .then((r) => ({
            data: r.data
          }))
          .catch((error) => ({ error }))
    }),
    statusTransitionMap: builder.query<StatusTransitionMap, void>({
      providesTags: ['HospitalSpecificData'],
      queryFn: () =>
        ProtocolService.getInstance()
          .getStatusTransitionMap()
          .then((data) => ({ data }))
          .catch((error) => ({ error }))
    }),
    protocolStatuses: builder.query<
      ProtocolStatuses | ProtocolStatusesWithMeta,
      { patientId: ID; includeMeta?: boolean }
    >({
      providesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId }
      ],
      queryFn: ({ patientId, includeMeta }) =>
        ProtocolService.getInstance()
          .fetchStatuses(patientId, includeMeta)
          .then((r) => ({
            data: r.data
          }))
          .catch((error) => ({ error }))
    }),

    protocol: builder.query<
      Protocol,
      { patientId: ID; protocolName: ProtocolName }
    >({
      queryFn: ({ patientId, protocolName }) =>
        ProtocolService.getInstance()
          .fetchProtocol(protocolName, patientId)
          .then((r) => ({
            data: r.data
          }))
          .catch((error) => ({ error })),
      providesTags: (result, error, arg) => [
        {
          type: 'Protocol',
          id: arg.patientId
        },
        {
          type: 'Protocol',
          id: arg.protocolName
        },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            protocol: arg.protocolName,
            patientId: arg.patientId
          })
        }
      ]
    }),

    updateProtocol: builder.mutation<Protocol, ProtocolPostPayload>({
      queryFn: async (payload, api) => {
        const transitionConfirmed = await confirmTransitionIfNeeded(
          payload,
          api
        );
        if (!transitionConfirmed) {
          return {
            data: {} as any
          };
        }
        return ProtocolService.getInstance()
          .updateProtocol(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error }));
      },
      invalidatesTags: (result, error, arg) => {
        const arr: InvalidateArr = [];
        if (result?.id) {
          arr.push(
            'Dashboard',
            { type: 'ProtocolStatuses', id: arg.patientId },
            {
              type: 'Protocol',
              id: arg.protocol
            },
            {
              type: 'Protocol',
              id: constructProtocolCacheKey(arg)
            },
            { type: 'MedicalDocument', id: `${arg.patientId}_prescription` }
          );

          if (
            arg.protocol === ProtocolName.POSTOP_CHRONIC_PAIN &&
            arg.targetStatus ===
              PostopChronicPainProtocolStatus.CONSULTATION_PENDING
          ) {
            arr.push({ type: 'Scores', id: arg.patientId });
          }
        }

        return arr;
      }
    }),

    addProtocolItems: builder.mutation<ProtocolItem[], ProtocolItemPayload>({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .addProtocolItems(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey(arg)
        }
      ]
    }),

    addLabOrderItems: builder.mutation<
      LabOrderItem[],
      Parameters<ProtocolService['addLabOrderItems']>[0]
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .addLabOrderItems(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.PREOP_LAB_TEST
          })
        }
      ]
    }),

    removeProtocolItems: builder.mutation<ProtocolItem[], ProtocolItemPayload>({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .removeProtocolItems(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey(arg)
        }
      ]
    }),

    setLabOrderItemsFromPreset: builder.mutation<
      ProtocolItem[],
      WithPatientId<{ preset: string }>
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .setLabOrderItemsFromPreset(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.PREOP_LAB_TEST
          })
        }
      ]
    }),

    triggerAnemiaCopilot: builder.mutation<
      unknown,
      Parameters<(typeof ProtocolService.prototype)['triggerAnemiaCopilot']>[0]
    >({
      queryFn: (patientd) =>
        ProtocolService.getInstance()
          .triggerAnemiaCopilot(patientd)
          .then(() => ({ data: {} }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        'Dashboard',
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.ANEMIA
          })
        },
        { type: 'MedicalDocument', id: arg.patientId } // TODO Why is this needed?
      ]
    }),

    sendInformedConsent: builder.mutation<
      unknown,
      Parameters<(typeof ProtocolService.prototype)['sendInformedConsent']>[0]
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .sendInformedConsent(payload)
          .then(() => ({ data: {} }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        'Dashboard',
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId!,
            protocol: ProtocolName.TELEHEALTH
          })
        },
        { type: 'MedicalDocument', id: `${arg.patientId}_medical` }
      ]
    }),

    assignEarlyPostopQuestionnaire: builder.mutation<
      unknown,
      Parameters<
        (typeof ProtocolService.prototype)['assignEarlyPostopQuestionnaire']
      >[0]
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .assignEarlyPostopQuestionnaire(payload)
          .then(() => ({ data: {} }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.EARLY_POSTOP
          })
        },
        { type: 'Notification', id: arg.patientId }
      ]
    }),

    toggleAutoQuestionnairesSuspended: builder.mutation<
      unknown,
      Parameters<
        (typeof ProtocolService.prototype)['toggleAutoQuestionnaireAssignementsSuspended']
      >[0]
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .toggleAutoQuestionnaireAssignementsSuspended(payload)
          .then(() => ({ data: {} }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: arg.protocol
          })
        }
      ]
    }),

    upsertPrescription: builder.mutation<
      GenericPrescription,
      WithPatientId<GenericPrescriptionPayload>
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .upsertPrescription(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.GENERIC_PRESCRIPTION
          })
        }
      ],
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const optimisticUpdateResult = dispatch(
          protocolApi.util.updateQueryData(
            'protocol',
            {
              patientId: payload.patientId,
              protocolName: ProtocolName.GENERIC_PRESCRIPTION
            },
            (draft) => {
              draft.items = (draft.items as GenericPrescription[]).map((it) => {
                if (it.id === payload.prescriptionId) {
                  return {
                    ...it,
                    prescribedDrugs:
                      payload.drugs?.map((d, i) => ({ ...d, orderIndex: i })) ??
                      it.prescribedDrugs,
                    generalComments:
                      payload.generalComments ?? it.generalComments
                  };
                }

                return it;
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (err) {
          optimisticUpdateResult.undo();
          SentryService.getInstance().captureException(err);
        }
      }
    }),
    sendPrescription: builder.mutation<
      GenericPrescription,
      WithPatientId<GenericPrescriptionPayload>
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .sendPrescription(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => {
        const tags: InvalidateArr = [
          { type: 'ProtocolStatuses', id: arg.patientId },
          {
            type: 'Protocol',
            id: constructProtocolCacheKey({
              patientId: arg.patientId,
              protocol: ProtocolName.GENERIC_PRESCRIPTION
            })
          },
          { type: 'MedicalDocument', id: `${arg.patientId}_prescription` },
          { type: 'Notification', id: arg.patientId }
        ];

        if (arg.linkWithMeds) {
          tags.push({ type: 'Meds', id: arg.patientId });
        }

        return tags;
      }
    }),
    deletePrescription: builder.mutation<
      void,
      WithPatientId<GenericPrescription>
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .deletePrescription(payload)
          .then((resp) => ({ data: resp.data }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.GENERIC_PRESCRIPTION
          })
        }
      ]
    }),

    updateSurgeryPlanningComments: builder.mutation<
      void,
      WithPatientId<string>
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .updateProtocolComments({
            ...payload,
            protocol: ProtocolName.SURGERY_PLANNING
          })
          .then(() => ({ data: {} as any }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.SURGERY_PLANNING
          })
        }
      ],
      async onQueryStarted(
        { patientId, value: newComment },
        { dispatch, queryFulfilled, getState }
      ) {
        const relevantQueriesStates = Object.entries(
          getState().doctorBffApi.queries
        )
          .filter(([k]) => k.startsWith('surgeryPlanningPreop'))
          .filter(([, queryResult]) => {
            if (queryResult?.status !== 'fulfilled') {
              return false;
            }
            return !!(
              queryResult.data as Listed<PreopSurgeryPlanningDto>
            ).data?.find((d) => d.patient.id === patientId);
          })
          .map(([, queryResult]) => queryResult!);

        // const optimisticUpdateResults: PatchCollection[] = [];
        const optimisticUpdateResults: any[] = [];
        let anyCommentsNotBlankQuery = false;
        relevantQueriesStates.forEach((queryResult) => {
          const result = dispatch(
            dashboardApi.util.updateQueryData(
              queryResult.endpointName as any,
              queryResult.originalArgs,
              (_draft) => {
                const draft = _draft as Listed<PreopSurgeryPlanningDto>;
                const isCommentsNotBlankQuery =
                  (queryResult.originalArgs as SurgeryPlanningParams)
                    .comments === 'NOT_BLANK';
                anyCommentsNotBlankQuery ||= isCommentsNotBlankQuery;
                if (draft && isCommentsNotBlankQuery && !newComment) {
                  // if this patient exists in any {comments: NOT_BLANK} AND comment is deleted
                  // remove this patient
                  draft.data = draft.data?.filter(
                    (d) => d.patient.id !== patientId
                  );
                }

                const dto = draft?.data?.find(
                  (d) => d.patient.id === patientId
                );
                if (dto) {
                  dto.comments = newComment;
                }
              }
            )
          );

          optimisticUpdateResults.push(result);
        });

        try {
          await queryFulfilled;
          if (!anyCommentsNotBlankQuery && newComment) {
            // if this patient did not exist in any {comments: NOT_BLANK} AND comment is added
            // invalidate all present queries {comments: NOT_BLANK} so they're refetched
            dispatch(
              dashboardApi.util.invalidateTags([
                { type: 'Dashboard', id: 'NOT_BLANK_QUERY' }
              ])
            );
          }
        } catch (err) {
          optimisticUpdateResults.forEach((r) => r.undo());
          SentryService.getInstance().captureException(err);
        }
      }
    }),

    updatePreopChronicPainComments: builder.mutation<
      void,
      WithPatientId<string>
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .updateProtocolComments({
            ...payload,
            protocol: ProtocolName.PREOP_CHRONIC_PAIN
          })
          .then(() => ({ data: {} as any }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.PREOP_CHRONIC_PAIN
          })
        }
      ],
      async onQueryStarted(
        { patientId, value: newComment },
        { dispatch, queryFulfilled, getState }
      ) {
        const relevantQueriesStates = Object.entries(
          getState().doctorBffApi.queries
        )
          .filter(([k]) => k.startsWith('chronicPainPreop'))
          .filter(([, queryResult]) => {
            if (queryResult?.status !== 'fulfilled') {
              return false;
            }
            return !!(
              queryResult.data as Listed<PreopChronicPainDto>
            ).data?.find((d) => d.patient.id === patientId);
          })
          .map(([, queryResult]) => queryResult!);

        const optimisticUpdateResults: any[] = [];
        relevantQueriesStates.forEach((queryResult) => {
          const result = dispatch(
            dashboardApi.util.updateQueryData(
              queryResult.endpointName as any,
              queryResult.originalArgs,
              (_draft) => {
                const draft = _draft as Listed<PreopChronicPainDto>;

                const dto = draft?.data?.find(
                  (d) => d.patient.id === patientId
                );
                if (dto) {
                  dto.comments = newComment;
                }
              }
            )
          );

          optimisticUpdateResults.push(result);
        });

        try {
          await queryFulfilled;
        } catch (err) {
          optimisticUpdateResults.forEach((r) => r.undo());
          SentryService.getInstance().captureException(err);
        }
      }
    }),

    updatePostopChronicPainComments: builder.mutation<
      void,
      WithPatientId<string>
    >({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .updateProtocolComments({
            ...payload,
            protocol: ProtocolName.POSTOP_CHRONIC_PAIN
          })
          .then(() => ({ data: {} as any }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.POSTOP_CHRONIC_PAIN
          })
        }
      ],
      async onQueryStarted(
        { patientId, value: newComment },
        { dispatch, queryFulfilled, getState }
      ) {
        const relevantQueriesStates = Object.entries(
          getState().doctorBffApi.queries
        )
          .filter(([k]) => k.startsWith('chronicPainPostop'))
          .filter(([, queryResult]) => {
            if (queryResult?.status !== 'fulfilled') {
              return false;
            }
            return !!(
              queryResult.data as Listed<PostopChronicPainDto>
            ).data?.find((d) => d.patient.id === patientId);
          })
          .map(([, queryResult]) => queryResult!);

        // const optimisticUpdateResults: PatchCollection[] = [];
        const optimisticUpdateResults: any[] = [];
        relevantQueriesStates.forEach((queryResult) => {
          const result = dispatch(
            dashboardApi.util.updateQueryData(
              queryResult.endpointName as any,
              queryResult.originalArgs,
              (_draft) => {
                const draft = _draft as Listed<PostopChronicPainDto>;

                const dto = draft?.data?.find(
                  (d) => d.patient.id === patientId
                );
                if (dto) {
                  dto.comments = newComment;
                }
              }
            )
          );

          optimisticUpdateResults.push(result);
        });

        try {
          await queryFulfilled;
        } catch (err) {
          optimisticUpdateResults.forEach((r) => r.undo());
          SentryService.getInstance().captureException(err);
        }
      }
    }),
    updateTelehealthComments: builder.mutation<void, WithPatientId<string>>({
      queryFn: (payload) =>
        ProtocolService.getInstance()
          .updateProtocolComments({
            ...payload,
            protocol: ProtocolName.TELEHEALTH
          })
          .then(() => ({ data: {} as any }))
          .catch((error) => ({ error })),
      invalidatesTags: (result, error, arg) => [
        { type: 'ProtocolStatuses', id: arg.patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId: arg.patientId,
            protocol: ProtocolName.TELEHEALTH
          })
        }
      ]
    })
  })
});

export const {
  useProtocolStatusesQuery,
  useUpdateProtocolMutation: useUpdateProtocol,
  useUpdateSurgeryPlanningCommentsMutation: useUpdateSurgeryPlanningComments,
  useUpdatePreopChronicPainCommentsMutation: useUpdatePreopChronicPainComments,
  useUpdatePostopChronicPainCommentsMutation:
    useUpdatePostopChronicPainComments,
  useUpdateTelehealthCommentsMutation: useUpdateTelehealthComments,
  useProtocolQuery,
  useStatusTransitionMapQuery,
  useAddProtocolItemsMutation: useAddProtocolItems,
  useAddLabOrderItemsMutation: useLabOrderItems,
  useSetLabOrderItemsFromPresetMutation: useSetLabOrderItemsFromPreset,
  useRemoveProtocolItemsMutation: useDeleteProtocolItems,
  useSendInformedConsentMutation: useSendInformedConsent,
  useTriggerAnemiaCopilotMutation: useTriggerAnemiaCopilot,
  useAssignEarlyPostopQuestionnaireMutation: useAssignEarlyPostopQuestionnaire,
  useToggleAutoQuestionnairesSuspendedMutation:
    useToggleAutoQuestionnairesSuspended,
  useUpsertPrescriptionMutation: useUpsertPrescription,
  useSendPrescriptionMutation: useSendPrescription,
  useDeletePrescriptionMutation: useDeletePrescription,
  useLabOrderPresetsQuery: useLabOrderPresets
} = protocolApi;

const { useProtocolItemOptionsQuery } = protocolApi;

type ReturnValue<T extends ProtocolName> = ReturnType<
  typeof useProtocolItemOptionsQuery
> & {
  data?: ProtocolItemOptionForName<T>[];
  currentData?: ProtocolItemOptionForName<T>[];
};

export const useProtocolItemOptions = <T extends ProtocolName>(props: {
  protocol?: T;
}) => useProtocolItemOptionsQuery<ReturnValue<T>>(props);

export function constructProtocolCacheKey({
  protocol,
  patientId
}: {
  protocol: ProtocolName;
  patientId: ID;
}) {
  return `${protocol}_${patientId}`;
}

async function confirmTransitionIfNeeded(
  payload: ProtocolPostPayload,
  api: BaseQueryApi
): Promise<boolean> {
  if (payload.protocol === ProtocolName.POSTOP_CHRONIC_PAIN) {
    const currentStatus = await getCurrentPostopChronicPainStatus(
      payload.patientId,
      api
    );
    if (currentStatus === PostopChronicPainProtocolStatus.DN4_PENDING) {
      return (await imperativelyShowPromiseDialog(
        {
          dialogType: AppDialogType.CONFIRMATION_PROMISE,
          severity: 'warning',
          message: 'doctor-dashboard.postopPain.dn4CancelationDialog.body',
          title: 'doctor-dashboard.postopPain.dn4CancelationDialog.title'
        },
        api.dispatch
      )) as Promise<boolean>;
    }
  }

  return true;
}

async function getCurrentPostopChronicPainStatus(
  patientId: ID,
  api: BaseQueryApi
) {
  const selectCurrentProtocol = protocolApi.endpoints.protocol.select({
    patientId: patientId,
    protocolName: ProtocolName.POSTOP_CHRONIC_PAIN
  });
  let currentStatus = selectCurrentProtocol(api.getState() as RootState).data
    ?.status;

  if (!currentStatus) {
    const relevantQueries = Object.entries(
      (api.getState() as RootState).doctorBffApi.queries
    )
      .filter(
        ([k]) =>
          k.startsWith('chronicPainPostop') &&
          k.includes(PostopChronicPainProtocolStatus.DN4_PENDING)
      )
      .map(([, queryResult]) => queryResult);

    if (relevantQueries.length) {
      for (const queryResult of relevantQueries) {
        if (queryResult?.data) {
          const dtos = (queryResult?.data as any)
            .data as PostopChronicPainDto[];
          for (const dto of dtos) {
            if (dto.patient.id === patientId) {
              currentStatus = dto.statuses?.postopChronicPainProtocolStatus;
              break;
            }
          }
        }
      }
    } else {
      currentStatus = (
        await api.dispatch(
          protocolApi.endpoints.protocol.initiate(
            {
              patientId,
              protocolName: ProtocolName.POSTOP_CHRONIC_PAIN
            },
            { subscribe: false }
          )
        )
      ).data?.status;
    }
  }

  return currentStatus;
}
